import React from "react";
import { Home } from "lucide-react";
import type { Crumb } from "../../../types";

export type { Crumb } from "../../../types";

export type BreadcrumbsLinkComponent = React.ComponentType<{
    href: string;
    children?: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    [key: string]: any;
}>;

export type BreadcrumbsProps = {
    crumbs: Crumb[];
    LinkComponent?: BreadcrumbsLinkComponent;
    className?: string;
    ariaLabel?: string;
    showHomeIcon?: boolean;
    homeAccent?: string;
    currentPageAccent?: string;
};

const DefaultLink: BreadcrumbsLinkComponent = ({ href, children, ...props }) => (
    <a href={href} {...props}>
        {children}
    </a>
);

const Breadcrumbs = ({
    crumbs,
    LinkComponent = DefaultLink,
    className,
    ariaLabel = "Breadcrumb",
    showHomeIcon = true,
    homeAccent,
    currentPageAccent,
}: BreadcrumbsProps) => {
    const navClassName = ["w-full py-2 px-4 rounded-md", className].filter(Boolean).join(" ");

    return (
        <nav className={navClassName} aria-label={ariaLabel}>
            <ol className="flex flex-wrap items-center space-x-2 text-gray-600 text-sm">
                {crumbs.map((crumb, index) => {
                    const isLast = index === crumbs.length - 1;
                    const isHome = index === 0;
                    const accentColor = isLast ? currentPageAccent : isHome ? homeAccent : undefined;
                    const crumbStyle = accentColor ? { color: accentColor } : undefined;
                    const homeIconStyle = homeAccent ? { color: homeAccent } : undefined;
                    const crumbClasses = "inline-flex items-center gap-1 align-middle text-sm leading-5";
                    const content = (
                        <>
                            {showHomeIcon && isHome ? (
                                <Home
                                    aria-hidden="true"
                                    className="block h-4 w-4 flex-shrink-0"
                                    strokeWidth={2}
                                    style={homeIconStyle}
                                />
                            ) : null}
                            <span className="leading-5">{crumb.title}</span>
                        </>
                    );

                    return (
                        <li key={index} className="flex items-center">
                            {!isLast && crumb.href ? (
                                <LinkComponent
                                    href={crumb.href}
                                    className={`${crumbClasses} hover:text-blue-600 hover:underline transition-colors duration-200`}
                                    style={crumbStyle}
                                >
                                    {content}
                                </LinkComponent>
                            ) : (
                                <span className={`${crumbClasses} text-gray-800`} style={crumbStyle}>
                                    {content}
                                </span>
                            )}
                            {!isLast && <span className="mx-2 inline-flex items-center text-sm leading-5">/</span>}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}

export default Breadcrumbs;
